// library/update — the Library's pure `update` (module contract) and its
// `inbound`. Model × Msg → Step: the next model, effects as data, and the
// Signals the root routes. The query text and the opened row belong to the
// frame, so the intents that change them are `Signal.FrameRequested`; the
// frame's new state comes back as `shown`, and only then does the module
// start the reads it implies. A late answer (another free text, another item)
// is ignored: a no-op step, not a refusal.

import { exhaustSwitchCase, match, Remote, Result } from "@bilda/utils";
import type { FrameTransition } from "../../app/frame.js";
import {
    askedWords,
    sameWords,
} from "../../components/Search/contentSearch.js";
import type { SearchResultKind } from "../../components/Search/searchModel.js";
import type { Inbound, Step, Update } from "../module.js";
import type { Signal } from "../signal.js";
import type { LibraryEffect } from "./effect.js";
import type { Detail, LibraryModel, LoadedKind, Opened } from "./model.js";
import type { LibraryMsg } from "./msg.js";

export type LibraryRefusal =
    | { readonly type: "no_item_open" }
    | { readonly type: "nothing_to_restore" };

export type LibraryStep = Step<LibraryModel, LibraryEffect>;
type Outcome = Result.Result<LibraryStep, LibraryRefusal>;

const step = (
    model: LibraryModel,
    effects: readonly LibraryEffect[] = [],
    signals: readonly Signal[] = [],
): Outcome => Result.ok({ model, effects, signals });

const frame = (model: LibraryModel, transition: FrameTransition): Outcome =>
    step(model, [], [{ _tag: "Signal.FrameRequested", transition }]);

const loadedKindOf = (kind: SearchResultKind): LoadedKind | undefined => {
    switch (kind) {
        case "source":
        case "package":
        case "file":
            return kind;
        case "material":
        case "conversation":
            return undefined;
    }
};

// A saved-search command: the last failure is cleared by the next try.
const write = (model: LibraryModel, effect: LibraryEffect): Outcome =>
    step({ ...model, smartFilterFailure: null }, [effect]);

// Read the opened item (again): keep what it showed while the read runs.
const reload = (model: LibraryModel): Outcome =>
    model.detail._tag === "Detail.Open"
        ? step(
              {
                  ...model,
                  detail: {
                      ...model.detail,
                      load: Remote.startLoad(model.detail.load),
                  },
              },
              [
                  {
                      type: "load_detail",
                      id: model.detail.id,
                      kind: model.detail.kind,
                  },
              ],
          )
        : Result.err({ type: "no_item_open" });

// The query the frame shows: new content words start their content search.
const showQuery = (
    model: LibraryModel,
    query: string,
    contentWords: readonly string[],
): {
    readonly model: LibraryModel;
    readonly effects: readonly LibraryEffect[];
} => {
    const words = askedWords(contentWords);
    if (sameWords(words, model.content.words))
        return { model: { ...model, query }, effects: [] };
    const asked = words.length > 0;
    return {
        model: {
            ...model,
            query,
            content: {
                words,
                hits: asked
                    ? Remote.startLoad(model.content.hits)
                    : Remote.notAsked,
            },
        },
        effects: asked ? [{ type: "search_content", words }] : [],
    };
};

// The row the frame shows opened: a new one is marked and its content read.
const showOpened = (
    model: LibraryModel,
    opened: Opened | null,
): {
    readonly model: LibraryModel;
    readonly effects: readonly LibraryEffect[];
} => {
    const id = opened?.id ?? null;
    if (id === model.openedId) return { model, effects: [] };
    const kind = opened === null ? undefined : loadedKindOf(opened.kind);
    const detail: Detail =
        opened === null || kind === undefined
            ? { _tag: "Detail.Idle" }
            : {
                  _tag: "Detail.Open",
                  id: opened.id,
                  kind,
                  load: Remote.loading,
              };
    return {
        model: { ...model, openedId: id, marked: id ?? model.marked, detail },
        effects:
            detail._tag === "Detail.Open"
                ? [{ type: "load_detail", id: detail.id, kind: detail.kind }]
                : [],
    };
};

// Does this msg end a deleted saved search's undo? The author's next action
// does; the frame's echo and the reads' answers do not. Total, so a new msg
// has to say.
const endsUndo: Record<LibraryMsg["type"], boolean> = {
    shown: false,
    query_edited: true,
    row_marked: true,
    row_entered: true,
    stepped: true,
    list_returned: true,
    sort_chosen: true,
    detail_retried: true,
    smart_filter_applied: true,
    smart_filter_saved: true,
    smart_filter_renamed: true,
    smart_filter_query_updated: true,
    smart_filter_deleted: false,
    smart_filter_restored: false,
    smart_filter_written: false,
    // A failed delete has nothing to undo.
    smart_filter_failed: true,
    content_found: false,
    content_failed: false,
    detail_loaded: false,
    detail_failed: false,
    world_changed: false,
};

export const updateLibrary: Update<
    LibraryModel,
    LibraryMsg,
    LibraryEffect,
    LibraryRefusal
> = (model, msg) =>
    updateMsg(
        endsUndo[msg.type] && model.deletedSmartFilter !== null
            ? { ...model, deletedSmartFilter: null }
            : model,
        msg,
    );

const updateMsg = (model: LibraryModel, msg: LibraryMsg): Outcome => {
    switch (msg.type) {
        case "shown": {
            const q = showQuery(model, msg.query, msg.contentWords);
            const o = showOpened(q.model, msg.opened);
            return step(o.model, [...q.effects, ...o.effects]);
        }
        case "query_edited":
            return frame(model, {
                type: "set-library-query",
                query: msg.query,
            });
        case "row_marked":
            return step({ ...model, marked: msg.id });
        case "row_entered":
            return frame(
                { ...model, marked: msg.id },
                {
                    type: "select-library-row",
                    selectedId: msg.id,
                    ...(msg.target !== undefined ? { target: msg.target } : {}),
                },
            );
        case "stepped":
            return frame(
                { ...model, marked: msg.id },
                { type: "select-library-row", selectedId: msg.id },
            );
        case "list_returned":
            return model.openedId === null
                ? Result.err({ type: "no_item_open" })
                : frame(model, { type: "select-library-row" });
        case "sort_chosen":
            return step({ ...model, sort: msg.sort }, [
                { type: "remember_sort", sort: msg.sort },
            ]);
        case "detail_retried":
            return reload(model);
        // A saved search applies its sort here and its query through the
        // frame (`?q=`), like a typed one.
        case "smart_filter_applied": {
            const { filter } = msg;
            return step(
                { ...model, sort: filter.sort, applied: filter.id },
                [{ type: "remember_sort", sort: filter.sort }],
                [
                    {
                        _tag: "Signal.FrameRequested",
                        transition: {
                            type: "set-library-query",
                            query: filter.query,
                        },
                    },
                ],
            );
        }
        // The writes save the search AS SHOWN (the query text, the sort).
        case "smart_filter_saved":
            return write(model, {
                type: "create_smart_filter",
                name: msg.name,
                query: model.query.trim(),
                sort: model.sort,
            });
        case "smart_filter_renamed":
            return write(model, {
                type: "rename_smart_filter",
                id: msg.id,
                name: msg.name,
            });
        case "smart_filter_query_updated":
            return write(model, {
                type: "update_smart_filter_query",
                id: msg.id,
                query: model.query.trim(),
                sort: model.sort,
            });
        case "smart_filter_deleted":
            return write(
                {
                    ...model,
                    applied: model.applied === msg.id ? null : model.applied,
                    deletedSmartFilter: msg.id,
                },
                { type: "delete_smart_filter", id: msg.id },
            );
        case "smart_filter_restored":
            return model.deletedSmartFilter === null
                ? Result.err({ type: "nothing_to_restore" })
                : write(
                      { ...model, deletedSmartFilter: null },
                      {
                          type: "restore_smart_filter",
                          id: model.deletedSmartFilter,
                      },
                  );
        // The Library (with its saved searches) is read again.
        case "smart_filter_written":
            return step(model, [], [{ _tag: "Signal.WorldChanged" }]);
        case "smart_filter_failed":
            return step({ ...model, smartFilterFailure: msg.error });
        case "content_found":
            return sameWords(msg.words, model.content.words)
                ? step({
                      ...model,
                      content: {
                          words: model.content.words,
                          hits: Remote.loaded(msg.answers),
                      },
                  })
                : step(model);
        case "content_failed":
            return sameWords(msg.words, model.content.words)
                ? step({
                      ...model,
                      content: {
                          words: model.content.words,
                          hits: Remote.failed(msg.error),
                      },
                  })
                : step(model);
        case "detail_loaded":
        case "detail_failed": {
            const { detail } = model;
            if (detail._tag !== "Detail.Open" || detail.id !== msg.id)
                return step(model);
            return step({
                ...model,
                detail: {
                    ...detail,
                    load:
                        msg.type === "detail_loaded"
                            ? Remote.loaded(msg.content)
                            : Remote.failed(msg.error),
                },
            });
        }
        case "world_changed": {
            // Resources committed behind the client: the open item and the
            // content hits are read again (the last values stay shown).
            const { words } = model.content;
            const research: readonly LibraryEffect[] =
                words.length === 0 ? [] : [{ type: "search_content", words }];
            const next: LibraryModel =
                words.length === 0
                    ? model
                    : {
                          ...model,
                          content: {
                              ...model.content,
                              hits: Remote.startLoad(model.content.hits),
                          },
                      };
            const item = reload(next);
            return Result.isOk(item)
                ? step(item.value.model, [...research, ...item.value.effects])
                : step(next, research);
        }
        default:
            return exhaustSwitchCase(msg);
    }
};

export const inboundLibrary: Inbound<LibraryMsg> = match<
    Signal,
    LibraryMsg | undefined
>({
    "Signal.NoticeRaised": () => undefined,
    "Signal.FrameRequested": () => undefined,
    "Signal.WorldChanged": () => ({ type: "world_changed" }),
    "Signal.ConversationStarted": () => undefined,
    "Signal.PendingGroundingFlushed": () => undefined,
    "Signal.ProposalLanded": () => undefined,
    "Signal.StopRequested": () => undefined,
});
